import { css, keyframes } from '@emotion/react'
import {
  CornerRadius,
  cssVar,
  ThemedFocusIndicator,
  gemVar,
  LineWeight,
  Shadow,
  ShadowElevation,
  Spacing,
  ThemedColors,
  Typography,
} from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lambda'
import { DAYS_IN_WEEK } from '../monthGrid'
import { CalendarSizes } from '../sizes'

const rootClass = 'opal-month-calendar'
export const selectors = {
  root: rootClass,
  bar: `${rootClass}-bar`,
  barCount: `${rootClass}-bar-count`,
  barTitle: `${rootClass}-bar-title`,
  cell: `${rootClass}-cell`,
  chip: `${rootClass}-chip`,
  chipBody: `${rootClass}-chip-body`,
  chipColumn: `${rootClass}-chip-column`,
  chipDetails: `${rootClass}-chip-details`,
  chipLine: `${rootClass}-chip-line`,
  chipMark: `${rootClass}-chip-mark`,
  chipMoment: `${rootClass}-chip-moment`,
  chipMomentLine: `${rootClass}-chip-moment-line`,
  chipMomentName: `${rootClass}-chip-moment-name`,
  chipName: `${rootClass}-chip-name`,
  chipNameSeparator: `${rootClass}-chip-name-separator`,
  chipQuietName: `${rootClass}-chip-quiet-name`,
  chipSpacer: `${rootClass}-chip-spacer`,
  chipTime: `${rootClass}-chip-time`,
  chipTitle: `${rootClass}-chip-title`,
  chipTitleLine: `${rootClass}-chip-title-line`,
  dayActions: `${rootClass}-day-actions`,
  dayHeader: `${rootClass}-day-header`,
  dayNumber: `${rootClass}-day-number`,
  overflow: `${rootClass}-overflow`,
  progress: `${rootClass}-progress`,
  chipSlot: `${rootClass}-chip-slot`,
  skeletonChip: `${rootClass}-skeleton-chip`,
  span: `${rootClass}-span`,
  week: `${rootClass}-week`,
  weekday: `${rootClass}-weekday`,
  weekdays: `${rootClass}-weekdays`,
} as const

export const cssVariables = {
  /** Height of one content card, which the card details a person asked for set. */
  cardBlockSize: `--${rootClass}-card-block-size`,
  /** Bar lanes above the chips in the current week row. */
  laneCount: `--${rootClass}-lane-count`,
  /** Height of one week row. */
  weekRowBlockSize: `--${rootClass}-week-row-block-size`,
  /** Fill and border of a bar tinted with a person's chosen color. */
  itemBorder: `--${rootClass}-item-border`,
  itemFill: `--${rootClass}-item-fill`,
  /**
   * The fill under the pointer. A bar and a chip each publish their own value
   * for it: a chip has no tint at rest and so takes a lighter one.
   */
  itemHoverFill: `--${rootClass}-item-hover-fill`,
  /** Lane of the week row the bar is drawn in. */
  barLane: `--${rootClass}-bar-lane`,
  /**
   * Height of one item in a day's chip column: a chip at row density, a card
   * at card density. It is what a row a span was drawn over is held open at,
   * and what the span's own offset down the column is counted in.
   */
  itemBlockSize: `--${rootClass}-item-block-size`,
  /** Row of the chip column a span is drawn in. */
  spanRow: `--${rootClass}-span-row`,
} as const

export const stateVariables = {
  card: 'is-card',
  clippedEnd: 'is-clipped-end',
  clippedStart: 'is-clipped-start',
  colorless: 'is-colorless',
  dimmed: 'is-dimmed',
  draggable: 'is-draggable',
  dropForbidden: 'is-drop-forbidden',
  dropTarget: 'is-drop-target',
  hovered: 'is-hovered',
  /** The item whose panel is open, in the design's focus state. */
  open: 'is-open',
  otherMonth: 'is-other-month',
  runStart: 'is-run-start',
  scrollWeeks: 'is-scroll-weeks',
  today: 'is-today',
  wide: 'is-wide',
} as const

/**
 * How far quickfind pushes back what it did not match. Low enough that the
 * matches jump out, high enough that the shape of the month is still readable —
 * quickfind dims, it never hides.
 */
const dimmedOpacity = 0.22

/**
 * The design rounds chips and the count badge to 3px, between Gem's `XSmall`
 * and `Small`.
 */
const itemCornerRadius = 3

/**
 * The layers of the grid, from the bottom up. A bar is lifted over the
 * backgrounds and borders of the cells it reaches across; a day header is
 * lifted over the bars and chips that scroll under it while it is pinned; the
 * ring that marks the cell under a drag is lifted over both, so nothing
 * crossing the cell can paint out its edges; the day-name row is over every
 * week; and the refresh hairline is over the lot.
 */
const barZIndex = 1
const dayHeaderZIndex = 2
const dropRingZIndex = 3
const weekdaysZIndex = 4
const progressZIndex = 5

/** Thin enough to read as a hint rather than as a piece of the calendar. */
const progressBlockSize = 3

/** How far a placeholder's highlight travels, and how wide its gradient is. */
const shimmerDistance = 600

const shimmer = keyframes({
  '0%': { backgroundPosition: `-${shimmerDistance}px 0` },
  '100%': { backgroundPosition: `${shimmerDistance}px 0` },
})

const progressSweep = keyframes({
  '0%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(300%)' },
})

const progressPulse = keyframes({
  '0%, 100%': { opacity: 0.3 },
  '50%': { opacity: 1 },
})

const laneStackBlockSize = `calc(${cssVar(cssVariables.laneCount)} * ${
  CalendarSizes.barBlockSize + CalendarSizes.laneGap
}px + ${CalendarSizes.laneStackGap}px)`

export const root = css({
  [cssVariables.cardBlockSize]: `${CalendarSizes.cardBlockSize}px`,
  [cssVariables.itemBlockSize]: `${CalendarSizes.chipBlockSize}px`,
  [cssVariables.weekRowBlockSize]: `${CalendarSizes.weekRowBlockSize}px`,
  backgroundColor: gemVar(ThemedColors.Surface1),
  blockSize: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  // Week rows grow to hold their bar lanes, so a lane-heavy month scrolls.
  ...modernWithFallback(
    [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
    'overflowBlock: auto'
  ),

  // A month already on screen is refreshed under a hairline, never behind a
  // spinner. It takes no room of its own — its height is given back by the
  // margin under it — so nothing below it moves when it comes and goes, and it
  // sticks to the top edge while the weeks scroll under.
  //
  // It clips its own sweep. The grid scrolls, and a bar sweeping past the
  // right edge would otherwise widen what the grid can scroll to, so a
  // horizontal scrollbar would flicker in and out under the weeks and rock the
  // whole grid up and down for as long as the refresh took.
  [`.${selectors.progress}`]: {
    blockSize: progressBlockSize,
    flex: 'none',
    insetBlockStart: 0,
    marginBlockEnd: -progressBlockSize,
    overflow: 'hidden',
    position: 'sticky',
    zIndex: progressZIndex,

    '&::before, &::after': {
      blockSize: progressBlockSize,
      content: '""',
      insetBlockStart: 0,
      insetInlineStart: 0,
      position: 'absolute',
    },
    '&::before': {
      backgroundColor: gemVar(ThemedColors.Surface3),
      inlineSize: '100%',
    },
    '&::after': {
      animation: `${progressSweep} 1.1s ease-in-out infinite`,
      backgroundColor: gemVar(ThemedColors.Primary6),
      inlineSize: '33%',
    },

    // A bar sweeping the width is more motion than the news deserves for
    // someone who has asked for less of it. It breathes in place instead.
    '@media (prefers-reduced-motion: reduce)': {
      '&::after': {
        animation: `${progressPulse} 1.6s ease-in-out infinite`,
        inlineSize: '100%',
      },
    },
  },

  // The day names hold the top of the grid while the weeks scroll under them.
  [`.${selectors.weekdays}`]: {
    display: 'grid',
    // A shrunk row would no longer be as tall as the offset the day headers
    // are pinned at.
    flex: 'none',
    gridTemplateColumns: `repeat(${DAYS_IN_WEEK}, minmax(0, 1fr))`,
    insetBlockStart: 0,
    position: 'sticky',
    zIndex: weekdaysZIndex,
  },
  [`.${selectors.weekday}`]: {
    ...Typography.T300,
    backgroundColor: gemVar(ThemedColors.Surface2),
    blockSize: CalendarSizes.weekdayRowBlockSize,
    borderBlockEnd: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    borderInlineEnd: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    boxSizing: 'border-box',
    color: gemVar(ThemedColors.TextPrimary),
    fontSize: 11,
    // 0.44px at 11px, the tracking the design carries.
    letterSpacing: '0.04em',
    lineHeight: '100%',
    paddingBlock: Spacing.XS * 1.5,
    paddingInline: Spacing.XS * 2.5,
    textAlign: 'start',
    textTransform: 'uppercase',
  },

  [`.${selectors.week}`]: {
    blockSize: cssVar(cssVariables.weekRowBlockSize),
    display: 'grid',
    // Rows are a fixed height so every month keeps the same shape; density is
    // handled by the per-day render budget, never by dropping data.
    flex: '0 0 auto',
    gridTemplateColumns: `repeat(${DAYS_IN_WEEK}, minmax(0, 1fr))`,
  },

  // Scroll mode: rows grow to show every chip inline, and the month scrolls.
  //
  // They also share out whatever room is left over, so a month with little in
  // it fills the grid rather than stopping partway down it. The browser works
  // this out from the height the grid was given. Where the grid was given none
  // and sizes itself around its weeks, nothing is left over and the rows stay
  // as tall as their contents — a floor measured in JavaScript would instead
  // grow the very height it was measured from, round after round.
  [`&.${stateVariables.scrollWeeks} .${selectors.week}`]: {
    blockSize: 'auto',
    flexGrow: 1,
    minBlockSize: cssVar(cssVariables.weekRowBlockSize),
  },

  [`.${selectors.cell}`]: {
    borderBlockEnd: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    borderInlineEnd: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minInlineSize: 0,
    paddingBlockEnd: CalendarSizes.cellPaddingBlockEnd,
    rowGap: CalendarSizes.chipGap,
    // Bars start in this cell and reach across the ones after it, so the cell
    // must not clip and must not open a stacking context that traps them.
    position: 'relative',

    '&:focus-visible': ThemedFocusIndicator,
    [`&.${stateVariables.otherMonth}`]: {
      backgroundColor: gemVar(ThemedColors.Surface2),

      // The header carries a background of its own so that it can be pinned,
      // so it has to be told about the cell's.
      [`.${selectors.dayHeader}`]: {
        backgroundColor: gemVar(ThemedColors.Surface2),
      },
      // Today can fall in a neighbouring month, and it keeps its filled marker
      // there. Dim only the plain numbers: this selector is more specific than
      // the one that puts contrast text on the marker, and would grey it out.
      [`.${selectors.dayNumber}:not(.${stateVariables.today})`]: {
        color: gemVar(ThemedColors.TextSecondary),
      },
    },
    // The cell being dragged over says so, and says whether it will take what
    // is being held there.
    [`&.${stateVariables.dropTarget}`]: {
      backgroundColor: gemVar(ThemedColors.Surface3),

      [`.${selectors.dayHeader}`]: {
        backgroundColor: gemVar(ThemedColors.Surface3),
      },
    },
    [`&.${stateVariables.dropForbidden}`]: { cursor: 'not-allowed' },

    // The ring is drawn on a layer over the cell rather than as an inset shadow
    // on the cell itself, which the bars crossing the cell paint over. It takes
    // no space and no pointer events, so the drag it marks reaches the cell.
    [`&.${stateVariables.dropTarget}::after, &.${stateVariables.dropForbidden}::after`]:
      {
        content: '""',
        inset: 0,
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: dropRingZIndex,
      },
    [`&.${stateVariables.dropTarget}::after`]: {
      boxShadow: `inset 0 0 0 ${LineWeight.Large}px ${gemVar(ThemedColors.Primary6)}`,
    },
    [`&.${stateVariables.dropForbidden}::after`]: {
      boxShadow: `inset 0 0 0 ${LineWeight.Large}px ${gemVar(ThemedColors.Error1)}`,
    },

    // The day's actions stay out of the way until a person is on the day. The
    // grid says which day that is: a bar is drawn inside the cell of the day it
    // starts on and reaches over the days after it, so `:hover` would mark the
    // starting day whenever the pointer crosses a bar, and the day under the
    // pointer would lose its actions.
    [`&.${stateVariables.hovered} .${selectors.dayActions}, &:focus-within .${selectors.dayActions}`]:
      {
        opacity: 1,
        pointerEvents: 'auto',
      },
  },

  // Each week's dates hold under the day names while any of that week is on
  // screen, and are pushed off by the next week's. The cell is the header's
  // containing block, so a week lets go of its dates exactly when its last row
  // of chips leaves. The background is what the bars and chips scroll under.
  [`.${selectors.dayHeader}`]: {
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface1),
    blockSize: CalendarSizes.dayHeaderBlockSize,
    boxSizing: 'border-box',
    display: 'flex',
    flex: 'none',
    insetBlockStart: CalendarSizes.weekdayRowBlockSize,
    justifyContent: 'space-between',
    paddingBlockEnd: Spacing.XS / 2,
    paddingBlockStart: Spacing.XS,
    paddingInline: Spacing.XS * 1.5,
    position: 'sticky',
    zIndex: dayHeaderZIndex,
  },

  [`.${selectors.dayActions}`]: {
    alignItems: 'center',
    // Centers the 24px buttons in the 26px header: 1px clear on each side.
    alignSelf: 'center',
    display: 'flex',
    gap: 2,
    opacity: 0,
    pointerEvents: 'none',

    // The create button anchors the create popover, so the actions stay visible
    // while the popover it opened is up — even after the pointer wanders off
    // the cell.
    '&:has([data-state="open"])': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },

  [`.${selectors.dayNumber}`]: {
    ...Typography.T300,
    alignItems: 'center',
    background: 'none',
    blockSize: CalendarSizes.dayNumberBlockSize,
    border: 'none',
    // Gem has no pill token, and today's marker has to stay a circle.
    borderRadius: 999,
    color: gemVar(ThemedColors.TextPrimary),
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    minInlineSize: CalendarSizes.dayNumberBlockSize,
    padding: 0,

    '&:hover': { backgroundColor: gemVar(ThemedColors.Surface3) },
    '&:focus-visible': ThemedFocusIndicator,
    [`&.${stateVariables.today}`]: {
      backgroundColor: gemVar(ThemedColors.Primary6),
      color: gemVar(ThemedColors.TextContrast),

      '&:hover': { backgroundColor: gemVar(ThemedColors.Primary6) },
    },
  },

  [`.${selectors.chipColumn}`]: {
    display: 'flex',
    flexDirection: 'column',
    // The column takes what the cell has left, which is what holds the "+N
    // more" button against the bottom of the day.
    flex: 1,
    gap: CalendarSizes.chipGap,
    minBlockSize: 0,
    paddingInline: CalendarSizes.itemInsetInline,
    // Clears the bar lanes floating above this column.
    paddingBlockStart: laneStackBlockSize,
  },

  // Stands in for a chip nobody has been given yet, at the height of the one
  // that will replace it, so the first answer does not move the day about.
  [`.${selectors.skeletonChip}`]: {
    animation: `${shimmer} 1.6s linear infinite`,
    background: `linear-gradient(to right, ${gemVar(
      ThemedColors.Surface2
    )} 10%, ${gemVar(ThemedColors.Surface3)} 25%, ${gemVar(
      ThemedColors.Surface2
    )} 50%)`,
    backgroundSize: `${shimmerDistance}px 100%`,
    blockSize: CalendarSizes.chipBlockSize,
    borderRadius: itemCornerRadius,
    flexShrink: 0,
    inlineSize: '100%',

    // A second placeholder on the same day is drawn short, the way a list of
    // real titles ends.
    '&:nth-of-type(2)': { inlineSize: '70%' },

    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      background: gemVar(ThemedColors.Surface2),
    },
  },

  // A content span is the content's own chip stretched across the days it is
  // live on. It is lifted out of the chip column so that it can reach over the
  // days after this one, and the column keeps an empty row where it sits.
  [`.${selectors.span}`]: {
    display: 'flex',
    insetBlockStart: `calc(${
      CalendarSizes.dayHeaderBlockSize + CalendarSizes.chipGap
    }px + ${laneStackBlockSize} + ${cssVar(cssVariables.spanRow)} * (${cssVar(
      cssVariables.itemBlockSize
    )} + ${CalendarSizes.chipGap}px))`,
    position: 'absolute',
    zIndex: barZIndex,
  },

  // The row a span was drawn over. Holding it open is what keeps the chips
  // under a span level with the chips of the days beside it.
  [`.${selectors.chipSlot}`]: {
    blockSize: cssVar(cssVariables.itemBlockSize),
    flexShrink: 0,
  },

  [`.${selectors.bar}`]: {
    ...Typography.T300,
    alignItems: 'center',
    backgroundColor: cssVar(cssVariables.itemFill),
    blockSize: CalendarSizes.barBlockSize,
    border: 'none',
    borderRadius: CornerRadius.Small,
    boxSizing: 'border-box',
    color: gemVar(ThemedColors.TextPrimary),
    // The marks at the head of a bar sit flush against each other; the title
    // and the count hold their own distance instead.
    columnGap: 0,
    cursor: 'pointer',
    display: 'flex',
    // The lane stack starts where the flow puts the chip column: one `chipGap`
    // under the header, which is the room the budget already reserves for it.
    // The day's actions fill the header to its bottom edge, so a bar drawn
    // flush against that edge would touch their hover outline.
    insetBlockStart: `calc(${
      CalendarSizes.dayHeaderBlockSize + CalendarSizes.chipGap
    }px + ${cssVar(cssVariables.barLane)} * ${
      CalendarSizes.barBlockSize + CalendarSizes.laneGap
    }px)`,
    paddingInline: Spacing.XS,
    position: 'absolute',
    textAlign: 'start',
    zIndex: barZIndex,

    '&:focus-visible': ThemedFocusIndicator,

    // A bar answers the pointer with a stronger tint of the same colour, on
    // the bar itself rather than on the whole day. The class is what marks the
    // other week rows the same moment runs through.
    //
    // The swatch down the leading edge keeps its resting colour. It is the one
    // thing on the row that says which moment this is, and lighting it up as
    // well made the whole bar read as a different bar.
    [`&:hover, &:focus-visible, &.${stateVariables.hovered}`]: {
      backgroundColor: cssVar(
        cssVariables.itemHoverFill,
        gemVar(ThemedColors.Surface2)
      ),
    },
    // The moment whose panel is open (Moment Card, State=Focus): its resting
    // tint, a hairline drawn inside so the bar keeps its size, and lifted a
    // step off the grid. The hairline is half the moment's colour, mixed into
    // the surface the way the fill is, so it marks the bar without outlining
    // it. It holds through a hover.
    [`&.${stateVariables.open}`]: {
      backgroundColor: cssVar(cssVariables.itemFill),
      boxShadow: `inset 0 0 0 ${LineWeight.Small}px color-mix(in oklab, ${cssVar(
        cssVariables.itemBorder,
        gemVar(ThemedColors.StrokeSecondary2)
      )} 50%, ${gemVar(ThemedColors.Surface1)}), ${
        Shadow[ShadowElevation.E1].boxShadow
      }`,
    },

    // The color a person chose, carried down the leading edge the same way the
    // chips underneath the bar carry it. The design holds the swatch in a 4px
    // slot: 3px of color, then 1px of space before the first mark.
    '&::before': {
      backgroundColor: cssVar(cssVariables.itemBorder, 'transparent'),
      blockSize: 12,
      borderRadius: CornerRadius.XSmall,
      content: '""',
      flex: 'none',
      inlineSize: 3,
      marginInlineEnd: 1,
    },

    // A clipped edge stays square, so a bar reads as continuing into the next
    // or previous week rather than ending there. How far it runs is placed
    // with the bar, in `inlineStyles.ts`.
    [`&.${stateVariables.clippedStart}`]: {
      borderStartStartRadius: 0,
      borderEndStartRadius: 0,
    },
    [`&.${stateVariables.clippedEnd}`]: {
      borderStartEndRadius: 0,
      borderEndEndRadius: 0,
    },
    [`&.${stateVariables.dimmed}`]: { opacity: dimmedOpacity },
    // Draggable, but first of all a link: the pointer says "this opens",
    // and the drag still starts the moment it is pulled.
    [`&.${stateVariables.draggable}`]: { cursor: 'pointer' },
  },

  // The title holds only the room it needs so the count sits beside it rather
  // than at the far end of a bar that can be a week wide.
  [`.${selectors.barTitle}`]: {
    marginInlineStart: 2,
    maxInlineSize: 190,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  [`.${selectors.barCount}`]: {
    ...Typography.HL25,
    alignItems: 'center',
    // An opaque surface keeps the badge legible on any tint.
    backgroundColor: gemVar(ThemedColors.Surface1),
    blockSize: 12,
    borderRadius: itemCornerRadius,
    boxSizing: 'border-box',
    color: gemVar(ThemedColors.TextSecondary),
    display: 'inline-flex',
    flexShrink: 0,
    justifyContent: 'center',
    marginInlineStart: Spacing.XS,
    minInlineSize: 12,
    paddingInline: 2,
  },
  // <jcole@> As of 2026-05-11 Firefox doesn't support `text-box` or the properties it's shorthand for
  '@supports not (text-box-trim: trim-both)': {
    [`.${selectors.barCount}`]: {
      paddingBlockStart: 1,
    },
  },

  [`.${selectors.chip}`]: {
    ...Typography.T300,
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface1),
    blockSize: CalendarSizes.chipBlockSize,
    borderColor: gemVar(ThemedColors.StrokeSecondary1),
    borderRadius: itemCornerRadius,
    borderStyle: 'solid',
    borderWidth: LineWeight.Small,
    boxSizing: 'border-box',
    color: gemVar(ThemedColors.TextPrimary),
    columnGap: Spacing.XS / 2,
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    inlineSize: '100%',
    // A backstop, not the fix: the line heights below are what make the card
    // add up. This only guarantees that no future drift paints outside the
    // border a person can see.
    overflow: 'hidden',
    paddingInlineEnd: Spacing.XS,
    paddingInlineStart: Spacing.XS,
    textAlign: 'start',

    // The channel or account mark in front of the words. It fills its 12px box
    // where the workflow glyph after the words paints 10px of its own, so it
    // stands 4px off the words rather than the 2px everything else stands
    // apart, and the two ends of the row read as evenly spaced.
    [`.${selectors.chipMark}`]: {
      display: 'inline-flex',
      flex: 'none',
      marginInlineEnd: Spacing.XS / 2,
    },

    // The color the chip inherits from its moment, which is what ties the two
    // together on the grid. On a row it is held to the height of the text line
    // rather than the chip, so it reads as a mark beside the line, not a fill.
    '&::before': {
      backgroundColor: cssVar(cssVariables.itemBorder, 'transparent'),
      alignSelf: 'center',
      blockSize: 12,
      borderRadius: CornerRadius.XSmall,
      content: '""',
      flex: 'none',
      inlineSize: 3,
    },

    // A chip answers the pointer with the next surface down (Content Card,
    // State=Hover), never with a tint of its colour: a tinted chip reads as a
    // bar. The class marks the other week rows the same window runs through.
    [`&:hover, &.${stateVariables.hovered}`]: {
      backgroundColor: gemVar(ThemedColors.Surface2),
    },
    // The content whose panel is open (Content Card, State=Focus): its resting
    // face, a darker hairline, and lifted a step off the grid. It holds
    // through a hover.
    [`&.${stateVariables.open}`]: {
      backgroundColor: gemVar(ThemedColors.Surface1),
      borderColor: gemVar(ThemedColors.StrokeSecondary2),
      ...Shadow[ShadowElevation.E1],
    },
    '&:focus-visible': ThemedFocusIndicator,
    // A chip with nowhere to send a click renders as a span: a label, not a
    // control, so the pointer makes no promise.
    'span&': { cursor: 'default' },
    // A clipped edge stays square, so the chip reads as carrying on into the
    // week before or after rather than ending there.
    [`&.${stateVariables.clippedStart}`]: {
      borderStartStartRadius: 0,
      borderEndStartRadius: 0,
    },
    [`&.${stateVariables.clippedEnd}`]: {
      borderStartEndRadius: 0,
      borderEndEndRadius: 0,
    },
    // A chip with no label color gives the swatch's room to its contents
    // rather than holding an empty slot open.
    [`&.${stateVariables.colorless}::before`]: { display: 'none' },
    [`&.${stateVariables.dimmed}`]: { opacity: dimmedOpacity },
    // The first chip of a run stands further off the chip above it than the
    // chips of one run stand off each other. The extra space is the whole
    // divider: a sorted day reads as blocks, not as a ruled list.
    [`&.${stateVariables.runStart}`]: {
      marginBlockStart: CalendarSizes.runGap,
    },
    // Draggable, but first of all a link: the pointer says "this opens",
    // and the drag still starts the moment it is pulled.
    [`&.${stateVariables.draggable}`]: { cursor: 'pointer' },

    // A card is the same chip with room for the lines under the title. Every
    // card stands at the height the day's budget already reserved for it,
    // whatever its title runs to, so a day of cards reads as one shape
    // repeated rather than as a ragged column.
    [`&.${stateVariables.card}`]: {
      alignItems: 'stretch',
      blockSize: cssVar(cssVariables.cardBlockSize),
      borderRadius: CornerRadius.Small,
      paddingBlock: CalendarSizes.cardPaddingBlock,
      paddingInline: CalendarSizes.cardPaddingBlock,

      // On a card the color runs the full height, tying every line to it.
      '&::before': {
        alignSelf: 'stretch',
        blockSize: 'auto',
      },
    },
  },

  [`.${selectors.chipBody}`]: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minInlineSize: 0,
    rowGap: CalendarSizes.cardRowGap,
  },
  // Every line is held to a height of its own rather than left to the tallest
  // thing inside it. The workflow mark is taller than the 12px title line and
  // overhangs it on both sides by design; a line that grew to the mark would
  // put the last line through the bottom of the card.
  [`.${selectors.chipLine}`]: {
    alignItems: 'center',
    blockSize: CalendarSizes.cardLineBlockSize,
    columnGap: Spacing.XS / 2,
    display: 'flex',
    flex: 'none',
    lineHeight: `${CalendarSizes.cardLineBlockSize}px`,
    minInlineSize: 0,

    // The lines under the title carry the same TL300 the title does, quieter
    // only in the secondary color.
    '&:not(:first-of-type)': {
      color: gemVar(ThemedColors.TextSecondary),
    },
    // The moment line is named rather than counted. It sits second on a card
    // with a moment and nowhere at all on a card without one, so any rule that
    // went by position would squash the account line on the cards with no
    // moment.
    [`&.${selectors.chipMomentLine}`]: {
      blockSize: CalendarSizes.cardMomentLineBlockSize,
      lineHeight: `${CalendarSizes.cardMomentLineBlockSize}px`,
    },
    // The title runs to two lines and stops. It holds both of them whether or
    // not the words need the second, so the moment and the account sit at the
    // same height on every card. The time and the marks beside it stay on the
    // first of the two.
    [`&.${selectors.chipTitleLine}`]: {
      alignItems: 'start',
      blockSize: CalendarSizes.cardLineBlockSize * CalendarSizes.cardTitleLines,

      [`.${selectors.chipTitle}`]: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: CalendarSizes.cardTitleLines,
        whiteSpace: 'normal',
      },
    },
  },
  // The moment a card belongs to has a line of its own, so it takes the width
  // rather than competing with the account name for it.
  [`.${selectors.chipMoment}`]: {
    alignItems: 'center',
    // The moment mark's box carries the space before the name; see the size.
    columnGap: 0,

    // The box's padding would also indent the mark's ink past the marks and
    // text on the lines around it, so the box is pulled left by that much.
    '& > :first-child': {
      marginInlineStart:
        (CalendarSizes.cardDetailIconSize - CalendarSizes.cardMomentIconSize) /
        2,
    },
    display: 'inline-flex',
    flex: 1,
    minInlineSize: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  [`.${selectors.chipTitle}`]: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  [`.${selectors.chipMomentName}`]: {
    minInlineSize: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // A row packs its names from the leading edge. Neither of them takes the
  // room left over and neither is capped: they shrink together, each with an
  // ellipsis of its own, so a long push name cannot squeeze out the name of
  // the content beside it.
  //
  // How few characters each name may shrink to is a question about that name,
  // so `ContentChip` sets the floor on the element. This is the base it
  // overrides.
  [`.${selectors.chip} > .${selectors.chipName}`]: {
    flex: '0 1 auto',
    minInlineSize: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // The second of the two names is set the way a card's lines under the title
  // are: the same size and weight, quieter in the secondary colour.
  [`.${selectors.chipQuietName}`]: {
    color: gemVar(ThemedColors.TextSecondary),
  },
  [`.${selectors.chipNameSeparator}`]: {
    color: gemVar(ThemedColors.TextSecondary),
    flex: 'none',
  },
  // A window drawn over several days is far wider than what it holds, so its
  // details follow the names instead of standing a day's width away from them.
  // A window on one day is a chip like any other and keeps its trailing
  // cluster; on a row the spacer is left out rather than overruled.
  [`.${selectors.chip}.${stateVariables.wide} .${selectors.chipTitleLine} .${selectors.chipTitle}`]:
    {
      flex: '0 1 auto',
    },
  // What holds the names at the leading edge and the time and the marks at the
  // trailing one.
  [`.${selectors.chipSpacer}`]: {
    flex: '1 1 auto',
    minInlineSize: 0,
  },
  // The time and the workflow mark make one column down the trailing edge of a
  // card, held at the top of the two lines the title keeps.
  [`.${selectors.chipDetails}`]: {
    alignItems: 'center',
    blockSize: CalendarSizes.cardLineBlockSize,
    columnGap: Spacing.XS / 2,
    display: 'flex',
    flex: 'none',
  },
  // The time follows the names, quieter than they are. It is the last thing
  // on the row that can give up width: flex shares a shortfall by weight, so
  // the time's tiny one leaves the names to give theirs first, the first down
  // to its floor and the second to nothing. Only on a chip too narrow even
  // then does the time shorten, so the workflow mark beside it stays where it
  // is. A mark pushed off the chip would take the one thing on the row that
  // has no words to fall back on.
  [`.${selectors.chipTime}`]: {
    color: gemVar(ThemedColors.TextSecondary),
    flex: '0 0.001 auto',
    minInlineSize: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },

  [`.${selectors.overflow}`]: {
    ...Typography.T300,
    background: 'none',
    blockSize: CalendarSizes.overflowBlockSize,
    border: 'none',
    borderRadius: CornerRadius.XSmall,
    color: gemVar(ThemedColors.TextSecondary),
    cursor: 'pointer',
    flexShrink: 0,
    // The foot of the cell, whatever the day above it holds. The budget
    // already keeps this row; this only says where it goes.
    marginBlockStart: 'auto',
    paddingInline: Spacing.XS / 2,
    textAlign: 'start',

    '&:hover': { backgroundColor: gemVar(ThemedColors.Surface3) },
    '&:focus-visible': ThemedFocusIndicator,
  },
})

/**
 * What a chip stands for, read out when a person rests on it.
 *
 * It sits in a tooltip, which is portaled out of the grid and drawn on the
 * opposite theme, so it carries its own styles rather than living under `root`.
 */
export const itemTooltip = css({
  display: 'flex',
  flexDirection: 'column',
  maxInlineSize: Spacing.XS * 70,
  rowGap: Spacing.XS / 2,

  '& > :first-of-type': { ...Typography.H50 },
})
